import { PipingMaterialGroup } from './pipingMaterialGroups'

/**
 * Free-text piping `material` → API 579-1 Fig 4.5 material family.
 *
 * This mapping is deliberately conservative: a WRONG family flips which Fig
 * 4.5 temperature/NPS band is checked, which can silently promote a region to
 * Type A eligibility it should not have (safety-critical — see
 * `ffsComponentClassification.ts`'s Fig 4.5 gate). So on anything ambiguous,
 * unrecognised, or that could plausibly match more than one family, this
 * returns `undefined` rather than guessing. Callers must treat `undefined` as
 * "ask the engineer", never as a default.
 *
 * The two families and their member specs/alloys are per API 579-1/ASME
 * FFS-1 (2021) Fig 4.5 (imperial) / Fig 4.5M (metric):
 *  - (a) Carbon & Low Alloy Steels: plain carbon steel and Cr-Mo low-alloy
 *    steel piping specs (e.g. A106, A53, A333, A516, A234 WPB, API 5L,
 *    A335/A182 P/F-grades, A387).
 *  - (b) High Alloy & Nonferrous Steels: austenitic/duplex stainless, nickel
 *    alloys, titanium, and copper/aluminum nonferrous piping.
 *
 * Matching is case-insensitive and tolerant of separator noise ("SA-106B",
 * "SA106 B", "A 106" are all treated alike). Every pattern is word-bounded on
 * its LEFT so, e.g., "316" does not match inside an unrelated longer number;
 * numeric patterns use a "not immediately followed by another digit" check
 * (rather than a trailing `\b`) on the right so a directly-appended grade
 * letter still matches (e.g. "A106B" = ASTM A106 Grade B).
 */

// Order matters: HIGH_ALLOY patterns are checked first so a specific alloy
// token (e.g. "stainless", "inconel") wins over a generic "steel" mention
// that might also appear in the same string (e.g. "stainless steel").
const HIGH_ALLOY_PATTERNS: ReadonlyArray<RegExp> = [
  // Generic stainless / austenitic wording.
  /\bstainless\b/,
  /\bss\b/,
  /\baustenitic\b/,
  // Common austenitic stainless grades: 304/304L/316/316L/321/347 (with
  // optional "SS"/"TP" prefix and an optional trailing "L" for low-carbon).
  /\b(?:ss|tp)?[\s-]?3(?:04|16|21|47)l?(?![0-9])/,
  // Duplex / super-duplex grades.
  /\bduplex\b/,
  /\b2205(?![0-9])/,
  /\b2507(?![0-9])/,
  // Nickel alloys.
  /\binconel\b/,
  /\bincoloy\b/,
  /\bmonel\b/,
  /\bhastelloy\b/,
  /\balloy[\s-]?(?:600|625|800|825)(?![0-9])/,
  // Titanium.
  /\btitanium\b/,
  /\bti[\s-]?gr(?:ade)?[\s-]?\d+(?![0-9])/,
  // Copper / nonferrous.
  /\bcupronickel\b/,
  /\bcopper[\s-]?nickel\b/,
  /\bbrass\b/,
  /\bbronze\b/,
  /\baluminum\b/,
  /\baluminium\b/,
  // Stainless/nonferrous ASTM/ASME spec families: A240 (plate), A312 (pipe),
  // A182 (forgings) when explicitly a TP3xx grade, A358, and the B-series
  // (nonferrous — copper, nickel, titanium specs live in ASTM B-xxx).
  /\bs?a[\s-]?240(?![0-9])/,
  /\bs?a[\s-]?312(?![0-9])/,
  /\bs?a[\s-]?358(?![0-9])/,
  /\bs?a[\s-]?182[\s-]?tp[\s-]?3\d{2}l?(?![0-9])/,
  /\bs?b[\s-]?\d{2,4}(?![0-9])/,
]

// Carbon & low-alloy Cr-Mo steel spec families/grades. Deliberately does NOT
// include a bare "steel" or "carbon" pattern: those are too generic (alloy
// and stainless steels are also "steel"), and unqualified "steel" is treated
// as ambiguous/unknown, not carbon-low-alloy. Only an explicit "carbon steel"
// phrase or a recognised spec/grade counts.
const CARBON_LOW_ALLOY_PATTERNS: ReadonlyArray<RegExp> = [
  /\bcarbon[\s-]?steel\b/,
  /\bs?a[\s-]?106(?![0-9])/,
  /\bs?a[\s-]?53(?![0-9])/,
  /\bs?a[\s-]?333(?![0-9])/,
  /\bs?a[\s-]?516(?![0-9])/,
  /\bs?a[\s-]?234[\s-]?wpb\b/,
  /\bapi[\s-]?5l\b/,
  // Low-alloy Cr-Mo: explicit chromium content call-outs.
  /\b1[.,]?25[\s-]?cr\b/,
  /\b2[.,]?25[\s-]?cr\b/,
  /\b5[\s-]?cr\b/,
  /\b9[\s-]?cr\b/,
  // A335/A182 P/F-grades (seamless/forged Cr-Mo alloy steel).
  /\bs?a[\s-]?335[\s-]?p[\s-]?(?:5|9|11|22)(?![0-9])/,
  /\bs?a[\s-]?182[\s-]?f[\s-]?(?:5|9|11|22)(?![0-9])/,
  /\bs?a[\s-]?387(?![0-9])/,
]

function normalize(material: string): string {
  return material.trim().toLowerCase().replace(/_/g, ' ')
}

function matchesAny(patterns: ReadonlyArray<RegExp>, s: string): boolean {
  return patterns.some((p) => p.test(s))
}

/**
 * Map a free-text piping `material` string to its API 579 Fig 4.5 family.
 * Returns `undefined` when the string is empty, unrecognised, or ambiguous
 * (matches both families) — callers must treat that as "ask the engineer".
 */
export function pipingMaterialFamily(
  material: string
): PipingMaterialGroup | undefined {
  const s = normalize(material)
  if (s.length === 0) return undefined

  const isHighAlloy = matchesAny(HIGH_ALLOY_PATTERNS, s)
  const isCarbonLowAlloy = matchesAny(CARBON_LOW_ALLOY_PATTERNS, s)

  // Ambiguous: a string that specifically matched both families (e.g. a
  // nonsensical/garbled spec) → abstain rather than guess.
  if (isHighAlloy && isCarbonLowAlloy) return undefined
  if (isHighAlloy) return 'high-alloy-nonferrous'
  if (isCarbonLowAlloy) return 'carbon-low-alloy'

  // Nothing recognised (including a bare, unqualified "steel") → abstain.
  return undefined
}
